import path from "node:path";
import { exists, readJsonc, writeFileSafe } from "./filesystem.js";
import { createManagedBackup } from "./backup.js";
import { getTemplateFiles } from "./templates.js";

/**
 * @file opencode-merge.ts
 * @description Architecture & Registry for OpenCode configuration synthesis (`opencode.jsonc`).
 *
 * ## Architecture Overview
 * This module is responsible for initializing, updating, and merging the project's
 * `opencode.jsonc` file when `aw init` is executed in a consumer repository.
 *
 * ### Core Responsibilities:
 * 1. **Primary Agents Registry (`AGENTS`)**: Defines the 6 core workflow actors (Atlas, Nexus, Orion, Astra, Sage, Phoenix)
 *    and maps their instructions to `.ai-workflow/agents/<name>.md`.
 * 2. **Subagents Registry (`SUBAGENTS`)**: Registers domain-specific subagents mapped 1-to-1 with skills installed
 *    in `.ai-workflow/skills/<skill>/SKILL.md`. These subagents can be activated by OpenCode runtimes.
 * 3. **Commands Registry (`COMMANDS`)**: Maps slash-commands (e.g. `/atlas`, `/spec-create`, `/validate`) to their respective agent owners.
 * 4. **Safe JSONC Merging**: Merges AIWK configuration blocks into consumer repositories while preserving existing custom user
 *    settings in `opencode.jsonc` and managing backups.
 *
 * ### Registering a New Subagent:
 * To add a new subagent to the OpenCode runtime registry:
 * - Ensure a corresponding skill exists in `dist-assets/skills/<skill-name>/SKILL.md`.
 * - Add a new entry to the `SUBAGENTS` object with a PascalCase/Kebab name, a concise `description`, and the matching `skill` directory name.
 * - Note: 5 auxiliary skills (`cyber-security`, `database`, `devops`, `localization`, `performance`) are intentionally excluded
 *   from subagent registration as they serve as contextual lenses for existing agents (see `dist-assets/AGENTS.md`).
 */

const LEGACY_MARKER = "_aiWorkflowManaged";

interface AgentDef {
  description: string;
}

const AGENTS: Record<string, AgentDef> = {
  Atlas: { description: "Router and workflow coordinator for AI Workflow Kit" },
  Nexus: { description: "Discovery, requirement, scope, and specification owner" },
  Orion: { description: "Planning, PR sequencing, release, and deployment strategy owner" },
  Astra: { description: "Implementation owner for incremental, scoped changes" },
  Sage: { description: "Audit, validation, evidence, and quality gate owner" },
  Phoenix: { description: "Remediation and regression recovery owner" }
};

interface SubagentDef {
  description: string;
  skill: string;
}

const SUBAGENTS: Record<string, SubagentDef> = {
  "Architecture-Specialist": { description: "Select the simplest architecture that safely satisfies current requirements", skill: "architecture" },
  "Backend-Engineer": { description: "Support safe PHP/Node/Python backend changes for APIs and persistence", skill: "backend-development" },
  "Deployment-Specialist": { description: "Plan, review, or validate deployment, release, and production-readiness", skill: "deployment" },
  "Design-Specialist": { description: "Apply practical design principles to keep solutions simple and maintainable", skill: "design-principles" },
  "Docs-Engineer": { description: "Write or improve project documentation, READMEs, guides, and prompts", skill: "documentation" },
  "Frontend-Engineer": { description: "Implement or review frontend changes involving UI, state, routing, and accessibility", skill: "frontend-development" },
  "Frontend-Design-System-Specialist": { description: "Enforce CSS/HTML tokens, semantic elements, typography, and premium atmosphere constraints for frontend quality", skill: "frontend-design-system" },
  "Full-Stack-Engineer": { description: "Coordinate frontend and backend changes as one coherent, testable increment", skill: "full-stack-development" },
  "Token-Economist": { description: "Reduce context size while preserving information required for safe execution", skill: "optimize-tokens" },
  "PR-Manager": { description: "Plan, implement, review, or validate small pull requests while preserving scope", skill: "pr-workflow" },
  "Discovery-Analyst": { description: "Clarify ambiguous requests into actionable problem statements and success outcomes", skill: "product-discovery" },
  "Product-Planner": { description: "Turn clarified needs into scoped requirements with acceptance-ready outcomes", skill: "product-planning" },
  "Memory-Guardian": { description: "Manage discoverable project memory for durable decisions and recurring corrections", skill: "project-memory" },
  "Prompt-Engineer": { description: "Expert in creating and refining prompts for AI agents and workflows", skill: "prompt-engineer" },
  "QA-Engineer": { description: "Design, review, or improve tests, acceptance criteria, and validation evidence", skill: "qa-workflow" },
  "Refactoring-Specialist": { description: "Improve code/document structure safely while preserving behavior", skill: "refactoring" },
  "Release-Specialist": { description: "Prepare release readiness decisions with explicit gates and evidence", skill: "release-workflow" },
  "SDD-Specialist": { description: "Convert approved requirements into explicit, testable specification artifacts", skill: "spec-driven-development" },
  "Technical-Leader": { description: "Make technical decisions, review architecture, and identify trade-offs", skill: "technical-leadership" },
  "UI-UX-Engineer": { description: "Improve usability and interface clarity without introducing unnecessary complexity", skill: "ui-ux-design" }
};

interface CommandDef {
  description: string;
  agent: string;
}

const COMMANDS: Record<string, CommandDef> = {
  atlas: { description: "Decide safest next step and delegate to the appropriate workflow or agent", agent: "Atlas" },
  run: { description: "Execute the master orchestration pipeline (Gates, SDD, Implementation, Validation, Healing)", agent: "Atlas" },
  discover: { description: "Understand the project, gather context, and create initial documentation", agent: "Nexus" },
  "spec-create": { description: "Create a clear specification before implementation", agent: "Nexus" },
  "spec-review": { description: "Review a specification and validate scope, risks, criteria, and gaps", agent: "Nexus" },
  "spec-implement": { description: "Implement an approved specification incrementally", agent: "Astra" },
  plan: { description: "Plan a task or change, defining steps, risks, and validation", agent: "Orion" },
  implement: { description: "Implement a small task with defined scope", agent: "Astra" },
  validate: { description: "Run project validations and collect evidence", agent: "Sage" },
  audit: { description: "Audit the project, identify issues, and suggest prioritized improvements", agent: "Sage" },
  "optimize-tokens": { description: "Analyze workflow and context usage; suggest reductions without losing quality or evidence", agent: "Sage" },
  "update-memory": { description: "Update durable project memory with relevant decisions, constraints, and reusable context", agent: "Nexus" },
  release: { description: "Prepare delivery with commit, push, PR/merge readiness, changelog, and evidence", agent: "Orion" },
  deploy: { description: "Publish the project to the defined environment after validation and release readiness", agent: "Orion" }
};

interface ManagedAgentConfig {
  mode: "primary" | "subagent";
  description: string;
  prompt: string;
}

interface ManagedCommandConfig {
  description: string;
  agent: string;
  template: string;
}

interface ManagedConfig {
  $schema: string;
  default_agent: string;
  agent: Record<string, ManagedAgentConfig>;
  skills: {
    paths: string[];
  };
  command: Record<string, ManagedCommandConfig>;
  mcp?: Record<string, any>;
}

function buildManagedConfig(): ManagedConfig {
  const agent: Record<string, ManagedAgentConfig> = {};
  for (const [name, def] of Object.entries(AGENTS)) {
    agent[name] = {
      mode: "primary",
      description: def.description,
      prompt: `{file:./.ai-workflow/opencode/agents/${name.toLowerCase()}.md}`
    };
  }

  for (const [name, def] of Object.entries(SUBAGENTS)) {
    agent[name] = {
      mode: "subagent",
      description: def.description,
      prompt: `{file:./.ai-workflow/opencode/skills/${def.skill}/SKILL.md}`
    };
  }

  const command: Record<string, ManagedCommandConfig> = {};
  for (const [name, def] of Object.entries(COMMANDS)) {
    command[name] = {
      description: def.description,
      agent: def.agent,
      template: `{file:./.ai-workflow/opencode/commands/${name}.md}`
    };
  }

  return {
    $schema: "https://opencode.ai/config.json",
    default_agent: "Atlas",
    agent,
    skills: {
      paths: ["./.ai-workflow/opencode/skills"]
    },
    command
  };
}

export interface MergeOpencodeConfigOptions {
  force?: boolean;
  backupRoot?: string;
}

export interface MergeOpencodeConfigResult {
  changed: boolean;
  reason: string;
}

export async function mergeOpencodeConfig(
  cwd: string,
  { force = false, backupRoot = ".ai-workflow-backups" }: MergeOpencodeConfigOptions = {}
): Promise<MergeOpencodeConfigResult> {
  const targetPath = path.join(cwd, "opencode.jsonc");
  const managed = buildManagedConfig();

  if (!(await exists(targetPath))) {
    await writeFileSafe(targetPath, `${JSON.stringify(managed, null, 2)}\n`);
    return { changed: true, reason: "created" };
  }

  let current: any;
  try {
    current = await readJsonc(targetPath);
  } catch {
    if (!force) {
      return { changed: false, reason: "invalid-json (skipped)" };
    }
    await createManagedBackup(targetPath, {
      cwd,
      backupRoot,
      maxPerFile: 20
    });
    await writeFileSafe(targetPath, `${JSON.stringify(managed, null, 2)}\n`);
    return { changed: true, reason: "invalid-json (replaced with --force)" };
  }

  const { [LEGACY_MARKER]: _legacyManaged, ...currentWithoutLegacy } = current;

  const next = {
    ...currentWithoutLegacy,
    $schema: current.$schema ?? managed.$schema,
    ...(managed.mcp
      ? {
          mcp: {
            ...(current.mcp ?? {}),
            ...managed.mcp
          }
        }
      : {}),
    agent: {
      ...(current.agent ?? {}),
      ...managed.agent
    },
    command: {
      ...(current.command ?? {}),
      ...managed.command
    }
  };

  const changed = JSON.stringify(current) !== JSON.stringify(next);
  if (!changed) {
    return { changed: false, reason: "already-up-to-date" };
  }

  await writeFileSafe(targetPath, `${JSON.stringify(next, null, 2)}\n`);
  return { changed: true, reason: "merged-managed-sections" };
}
